import { BookOpen, GalleryVerticalEnd, Layers, Printer, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { Book } from '@/components/storybook/Book'
import { Chapters } from '@/components/storybook/Chapters'
import { PickedUp } from '@/components/storybook/PickedUp'
import { Table } from '@/components/storybook/Table'
import { useAlbum, type Album, type AlbumPhoto } from '@/lib/albums'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { saveStoryView, savedStoryView, STORY_VIEW_NAMES, STORY_VIEWS, type StoryView } from '@/lib/storybook'
import { cn } from '@/lib/utils'
import { ScreenLoader } from '@/components/Loader'

export function StorybookPage() {
  const { id = '' } = useParams()
  const album = useAlbum(id)
  if (album.isPending) return <ScreenLoader surface="viewer" />
  if (album.isError) return <FormError error={album.error} />
  return <Storybook album={album.data} />
}

const ICONS = { chapters: GalleryVerticalEnd, book: BookOpen, table: Layers }
const round =
  'grid size-11 place-items-center rounded-full outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/60'

// The Storybook version of an album, over everything else like the photo viewer. It always says what it is, so it's
// never taken for the album itself, and the reader picks how to go through it.
function Storybook({ album }: { album: Album }) {
  const [view, setView] = useState<StoryView>(savedStoryView)
  const [picked, setPicked] = useState<AlbumPhoto | null>(null)
  usePageFrame(useItemHome(album), {
    title: album.title,
    back: { to: `/album/${album.id}`, label: `Back to ${album.title}` },
  })
  const dark = view === 'chapters'
  const choose = (next: StoryView) => {
    setView(next)
    saveStoryView(next)
  }

  return (
    <article
      aria-label={`${album.title}, Storybook version`}
      className={cn(
        'fixed inset-0 z-50 overflow-hidden',
        dark ? 'bg-viewer text-viewer-foreground' : 'bg-table text-foreground',
      )}
    >
      {album.photos.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-title text-balance">There’s no story to tell until the album has photos.</p>
          <Link to={`/album/${album.id}`} className="text-sm underline underline-offset-4">
            Back to the album
          </Link>
        </div>
      ) : view === 'chapters' ? (
        <Chapters album={album} onPick={setPicked} />
      ) : view === 'book' ? (
        <Book album={album} onPick={setPicked} />
      ) : (
        <Table album={album} onPick={setPicked} />
      )}

      <header className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center gap-2 px-3 pt-[calc(var(--safe-top)+0.5rem)] sm:px-5">
        <Link
          to={`/album/${album.id}`}
          aria-label={`Close the storybook and go back to ${album.title}`}
          className={cn(
            round,
            'pointer-events-auto backdrop-blur-md',
            dark ? 'bg-viewer/55 hover:bg-viewer/80' : 'bg-background/80 shadow-paper hover:bg-background',
          )}
        >
          <X aria-hidden />
        </Link>
        <p
          className={cn(
            'mx-auto rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap backdrop-blur-md',
            dark ? 'bg-viewer/60' : 'bg-background/85 shadow-paper',
          )}
        >
          Storybook version
        </p>
        {view === 'book' && album.photos.length > 0 && (
          <Link
            to={`/album/${album.id}/print`}
            aria-label="Print this book"
            title="Print this book"
            className="pointer-events-auto flex h-11 items-center gap-1.5 rounded-full bg-background/85 px-3.5 text-sm shadow-paper backdrop-blur-md transition-colors outline-none hover:bg-background focus-visible:ring-[3px] focus-visible:ring-ring/60"
          >
            <Printer className="size-4" aria-hidden />
            <span className="hidden sm:inline">Print</span>
          </Link>
        )}
        {album.photos.length > 0 && (
          <div
            role="radiogroup"
            aria-label="How to read it"
            className={cn(
              'pointer-events-auto flex rounded-full p-1 backdrop-blur-md',
              dark ? 'bg-viewer/55' : 'bg-background/85 shadow-paper',
            )}
          >
            {STORY_VIEWS.map((v) => {
              const Icon = ICONS[v]
              return (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={view === v}
                  aria-label={STORY_VIEW_NAMES[v]}
                  title={STORY_VIEW_NAMES[v]}
                  onClick={() => choose(v)}
                  className={cn(
                    'flex h-9 min-w-9 items-center justify-center gap-1.5 rounded-full px-2 text-sm transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 sm:px-3',
                    view === v
                      ? dark
                        ? 'bg-viewer-foreground text-viewer'
                        : 'bg-primary text-primary-foreground'
                      : dark
                        ? 'hover:bg-viewer-foreground/12'
                        : 'hover:bg-accent',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  <span className="hidden sm:inline">{STORY_VIEW_NAMES[v]}</span>
                </button>
              )
            })}
          </div>
        )}
      </header>

      <PickedUp photo={picked} from={album.by ? `From the album of ${album.by}` : ''} onClose={() => setPicked(null)} />
    </article>
  )
}
